'use client';

import { useCallback, useRef } from 'react';
import { Quote } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useEventEmitter } from '@/hooks/useEventEmitter';
import { useInViewport } from 'ahooks';
import type { Message } from '@/type/chat';
import Copy from '../MessageActions/Copy';
import UserContextCard, { type UserContextType } from './UserContextCard';

const MESSAGE_BUBBLE_CLASS =
  'dark:text-white text-black font-light leading-[1.75] text-base dark:bg-[#1E1E1E] bg-[#EFEFEF] p-2.5 rounded-2xl';

interface MessageRequestProps {
  message: Message;
}

export default function MessageRequest({ message }: MessageRequestProps) {
  const ref = useRef<HTMLDivElement>(null);
  const emitter = useEventEmitter();

  const callback = useCallback(
    (entry: IntersectionObserverEntry) => {
      emitter.emit('messageInViewport', entry.isIntersecting ? 'add' : 'remove', message.messageId);
    },
    [emitter, message.messageId],
  );

  useInViewport(ref, {
    callback,
    threshold: 1,
  });

  return (
    <div ref={ref} className="w-full flex justify-end" id={message.messageId}>
      <div className="group">
        <MessageRequestContent message={message} />
        <div className="flex justify-end h-10">
          <div className="hidden group-hover:block p-1">
            <Copy text={message.content} />
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageRequestContent({ message }: MessageRequestProps) {
  if (message.quotedText) {
    return (
      <div className="flex flex-col items-end justify-end pt-8 wrap-break-word inline:w-auto">
        <div className="dark:bg-[#1E1E1E] bg-[#EFEFEF] rounded-xl px-3 py-2 mb-1.25 max-w-full">
          <div className="flex items-center gap-2">
            <Quote className="text-[#A772FF] rotate-180" size={16} />
            <p className="text-[14px] dark:text-white text-black line-clamp-2 flex-1">
              {message.quotedText}
            </p>
            <Quote className="text-[#A772FF]" size={16} />
          </div>
        </div>
        <MessageBubble>{message.content}</MessageBubble>
      </div>
    );
  }

  if (message.userContext?.data) {
    const contextType = message.userContext.context_type as UserContextType;
    return (
      <div className="flex flex-col gap-4 items-end justify-end wrap-break-word inline:w-auto">
        <UserContextCard userContext={message.userContext.data} contextType={contextType} />
        <MessageBubble>{message.content}</MessageBubble>
      </div>
    );
  }

  return <MessageBubble className="text-text-primary">{message.content}</MessageBubble>;
}

function MessageBubble({ children, className }: { children: string; className?: string }) {
  return <div className={cn(MESSAGE_BUBBLE_CLASS, className)}>{children}</div>;
}
